import { FC, useMemo } from 'react';
import { DateRange } from 'react-day-picker';
import { cn, getFormattedDate } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import DateRangePicker from './DateRangePicker';
import SingleDatePicker from './SingleDatePicker';

type SetStateFunction = (newValue: string) => void;

interface DateFilterParams {
  setSelectedDate?: SetStateFunction;
  selectedDate?: string | undefined;
  setStartDate?: SetStateFunction;
  startDate?: string | undefined;
  setEndDate?: SetStateFunction;
  endDate?: string | undefined;
  mode?: 'single' | 'range';
  setFilters?: SetStateFunction;
  hidePopup?: boolean;
  className?: string;
}

const DateFilter: FC<DateFilterParams> = ({
  setSelectedDate,
  selectedDate,
  mode = 'single',
  setStartDate,
  startDate,
  setEndDate,
  endDate,
  hidePopup,
  className,
}) => {
  const navigate = useNavigate();
  const currentUrl = new URL(window.location.href);
  const params = new URLSearchParams(currentUrl.search);

  const updateUrlAndNavigate = () => {
    currentUrl.search = params.toString();
    window.history.pushState({}, '', currentUrl);
    navigate(currentUrl.pathname + currentUrl.search);
  };

  const handleSingleDateChange = (date: Date | undefined) => {
    if (date) {
      setSelectedDate?.(getFormattedDate(date));
      params.set('date', getFormattedDate(date));
    } else {
      setSelectedDate?.('');
      params.delete('date');
    }
    updateUrlAndNavigate();
  };

  const handleRangeChange = (range: DateRange | undefined) => {
    params.set('pageIndex', '0');

    if (range?.from && range.to) {
      setStartDate?.(getFormattedDate(range.from));
      setEndDate?.(getFormattedDate(range.to));
      params.set('startDate', getFormattedDate(range.from));
      params.set('endDate', getFormattedDate(range.to));
    } else {
      setStartDate?.('');
      setEndDate?.('');
      params.delete('startDate');
      params.delete('endDate');
    }

    updateUrlAndNavigate();
  };

  const fecha = useMemo(() => {
    const range: DateRange = { from: undefined, to: undefined };
    if (selectedDate) {
      const [year, month, day] = selectedDate.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    if (startDate) {
      const [year, month, day] = startDate.split('-').map(Number);
      range.from = new Date(year, month - 1, day);
    }
    if (endDate) {
      const [year, month, day] = endDate.split('-').map(Number);
      range.to = new Date(year, month - 1, day);
    }
    return range;
  }, [startDate, endDate, selectedDate]);

  if (mode === 'range') {
    return (
      <DateRangePicker
        value={fecha as DateRange}
        onChange={handleRangeChange}
        placeholder="Filtrar Período"
        align="end"
        triggerVariant="secondary"
        className={cn(className, startDate && endDate ? 'bg-green-100 hover:bg-green-100' : '')}
        badgeText={!hidePopup && startDate && endDate ? 'Período Seleccionado' : undefined}
      />
    );
  }

  return (
    <SingleDatePicker
      value={fecha instanceof Date ? fecha : undefined}
      onChange={handleSingleDateChange}
      placeholder="Filtrar Período"
      align="end"
      triggerVariant="secondary"
      className={cn(className, selectedDate ? 'bg-green-100 hover:bg-green-100' : '')}
      badgeText={!hidePopup && selectedDate ? 'Período Seleccionado' : undefined}
    />
  );
};

export { DateFilter };
